import {coerceImageObject, type ImageInput} from "@/helpers/imageObject";
import {getSiteUrl} from "@/helpers/functions";

export interface HowToStep {
    name: string;
    text: string;
    image?: ImageInput;
    url?: string;
}

export interface HowToToolOrSupply {
    name: string;
    image?: ImageInput;
}

export type HowToToolOrSupplyInput = string | HowToToolOrSupply;

export interface SeoSchemaHowToProps {
    name: string;
    description: string;
    steps: HowToStep[];
    totalTime?: string;
    tools?: HowToToolOrSupplyInput[];
    supplies?: HowToToolOrSupplyInput[];
}

const normalizeToolOrSupply = (entry: HowToToolOrSupplyInput): HowToToolOrSupply =>
    typeof entry === "string" ? {name: entry} : entry;

const toAbsoluteUrl = (siteUrl: string, url: string | undefined): string | undefined => {
    if (!url) return undefined;
    if (/^https?:\/\//i.test(url)) return url;
    const path = url.startsWith("/") ? url : `/${url}`;
    return `${siteUrl}${path}`;
};

export function SeoSchemaHowTo({
                                   name,
                                   description,
                                   steps,
                                   totalTime,
                                   tools,
                                   supplies,
                               }: SeoSchemaHowToProps) {
    const siteUrl = getSiteUrl();

    const schema: Record<string, unknown> = {
        "@context": "https://schema.org",
        "@type": "HowTo",
        "name": name,
        "description": description,
        "step": steps.map((step, idx) => {
            const stepSchema: Record<string, unknown> = {
                "@type": "HowToStep",
                "position": idx + 1,
                "name": step.name,
                "text": step.text,
            };
            if (step.image) stepSchema.image = coerceImageObject(step.image);
            const absUrl = toAbsoluteUrl(siteUrl, step.url);
            if (absUrl) stepSchema.url = absUrl;
            return stepSchema;
        }),
    };

    if (totalTime) schema.totalTime = totalTime;

    if (tools && tools.length) {
        schema.tool = tools.map(normalizeToolOrSupply).map((tool) => {
            const entry: Record<string, unknown> = {"@type": "HowToTool", "name": tool.name};
            if (tool.image) entry.image = coerceImageObject(tool.image);
            return entry;
        });
    }

    if (supplies && supplies.length) {
        schema.supply = supplies.map(normalizeToolOrSupply).map((supply) => {
            const entry: Record<string, unknown> = {"@type": "HowToSupply", "name": supply.name};
            if (supply.image) entry.image = coerceImageObject(supply.image);
            return entry;
        });
    }

    return (
        <script
            type="application/ld+json"
            dangerouslySetInnerHTML={{__html: JSON.stringify(schema)}}
        />
    );
}
